import { useEffect, useMemo, useRef } from "react"

import type { CitationTextAnchor } from "@/utils/citation-anchor-utils"

import { FallbackViewer } from "@/features/file-viewer/components/fallback-viewer"
import { useFileContentAsText } from "@/hooks/use-file-content"
import { getCitationTextPassage } from "@/utils/citation-anchor-utils"
import { scrollCitationHighlightIntoView } from "@/utils/dom-highlight"

type CitationExtractedTextViewerProps = {
  fileUrl: string
  textAnchor: CitationTextAnchor
  quoteSnippet?: string
  scrollContainer: HTMLElement | null
}

export function CitationExtractedTextViewer({
  fileUrl,
  textAnchor,
  quoteSnippet = "",
  scrollContainer,
}: CitationExtractedTextViewerProps) {
  const { data: content, isError, isLoading } = useFileContentAsText(fileUrl)
  const markRef = useRef<HTMLElement>(null)
  const text = content ?? ""
  const { charStart, charEnd } = textAnchor
  const passage = useMemo(
    () => getCitationTextPassage(text, { charStart, charEnd }, quoteSnippet),
    [text, charStart, charEnd, quoteSnippet]
  )

  useEffect(() => {
    if (!markRef.current) return
    return scrollCitationHighlightIntoView(markRef.current, {
      preferredContainer: scrollContainer,
      maxPasses: 2,
    })
  }, [passage, scrollContainer])

  if (isError) return <FallbackViewer fileUrl={fileUrl} />
  if (isLoading) return <div role="status">Loading…</div>

  return (
    <pre className="text-foreground font-sans text-sm break-words whitespace-pre-wrap">
      {passage ? (
        <>
          {passage.before}
          <mark
            ref={markRef}
            className="border-foreground bg-primary text-primary-foreground rounded-sm border"
          >
            {passage.passage}
          </mark>
          {passage.after}
        </>
      ) : (
        text
      )}
    </pre>
  )
}
